/**
 * Supabase Client 초기화 (선택적)
 * - Supabase 미사용 시에도 앱 정상 동작
 * - 자체 인증 시스템으로 전환됨 (2026-01)
 */
import { createClient, SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || ''
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || ''

/**
 * Supabase 클라이언트 (선택적)
 * - URL과 키가 없으면 null
 */
export const supabase: SupabaseClient | null = (supabaseUrl && supabaseAnonKey)
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: window.localStorage,
        storageKey: 'supabase.auth.token'
      }
    })
  : null

// 세션 변경 이벤트 리스너 (Supabase가 있을 때만)
if (supabase) {
  supabase.auth.onAuthStateChange((event, session) => {
    console.log('Supabase Auth Event:', event, session?.user?.id)

    if (event === 'SIGNED_IN') {
      console.log('✅ Supabase 로그인 성공:', session?.user?.email)
    } else if (event === 'SIGNED_OUT') {
      console.log('✅ Supabase 로그아웃 완료')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('auth_access_token')
      localStorage.removeItem('auth_refresh_token')
    } else if (event === 'TOKEN_REFRESHED') {
      console.log('✅ Supabase 토큰 갱신 완료')
      if (session?.access_token && session?.refresh_token) {
        localStorage.setItem('accessToken', session.access_token)
        localStorage.setItem('refreshToken', session.refresh_token)
        localStorage.setItem('auth_access_token', session.access_token)
        localStorage.setItem('auth_refresh_token', session.refresh_token)
      }
    }
  })
}

/**
 * 토큰 만료 여부 확인 (이미 만료됐거나 만료 5분 전이면 true)
 */
export const isTokenExpiredOrExpiringSoon = (): boolean => {
  const expiresAt = localStorage.getItem('auth_token_expires_at')
  if (!expiresAt) {
    return true
  }

  const expiryTime = new Date(expiresAt).getTime()
  const now = Date.now()
  const fiveMinutes = 5 * 60 * 1000

  return (expiryTime - now) < fiveMinutes
}

export const isTokenExpiringSoon = isTokenExpiredOrExpiringSoon

/**
 * Refresh Token으로 Access Token 갱신
 * - Supabase 없으면 자체 백엔드 API 사용
 *
 * ⭐ 동시 호출 가드 + 최소 호출 간격 (10초) — 폭주/429 방지
 */
let refreshInFlight: Promise<boolean> | null = null
let lastRefreshAt = 0
const MIN_REFRESH_INTERVAL_MS = 10 * 1000 // 10초 안에 또 부르면 무시

export const refreshAccessToken = async (): Promise<boolean> => {
  // 1) 이미 진행 중이면 그 Promise 재사용
  if (refreshInFlight) return refreshInFlight

  // 2) 직전에 성공적으로 갱신했으면 잠시 동안 거절 (rate guard)
  if (Date.now() - lastRefreshAt < MIN_REFRESH_INTERVAL_MS) {
    return true
  }

  const refreshToken = localStorage.getItem('refreshToken') || localStorage.getItem('auth_refresh_token')

  if (!refreshToken) {
    console.warn('⚠️ [Token] Refresh Token 없음')
    return false
  }

  refreshInFlight = (async () => {
  try {
    // Supabase가 있으면 Supabase 사용
    if (supabase) {
      const { data, error } = await supabase.auth.refreshSession({
        refresh_token: refreshToken
      })

      if (error || !data.session) {
        console.error('❌ [Token] Supabase 갱신 실패:', error)
        return false
      }

      const { access_token, refresh_token, expires_at } = data.session
      localStorage.setItem('accessToken', access_token)
      localStorage.setItem('auth_access_token', access_token)
      localStorage.setItem('refreshToken', refresh_token)
      localStorage.setItem('auth_refresh_token', refresh_token)

      if (expires_at) {
        const expiresAtDate = new Date(expires_at * 1000).toISOString()
        localStorage.setItem('auth_token_expires_at', expiresAtDate)
      }

      console.log('✅ [Token] Supabase로 Access Token 갱신 완료')
      return true
    }

    // Supabase 없으면 자체 백엔드 API 사용
    console.log('🔄 [Token] 자체 API로 토큰 갱신 시도...')
    const response = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) {
      console.error('❌ [Token] 자체 API 갱신 실패:', response.status)
      return false
    }

    const data = await response.json()
    if (data.access_token) {
      localStorage.setItem('accessToken', data.access_token)
      localStorage.setItem('auth_access_token', data.access_token)
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token)
        localStorage.setItem('auth_refresh_token', data.refresh_token)
      }
      if (data.expires_at) {
        localStorage.setItem('auth_token_expires_at', data.expires_at)
      } else if (data.expires_in) {
        // 백엔드가 expires_in(초)만 주는 경우, 절대 시각으로 변환해서 저장
        const expiresAt = new Date(Date.now() + data.expires_in * 1000).toISOString()
        localStorage.setItem('auth_token_expires_at', expiresAt)
      } else {
        // 최종 fallback: 1시간 가정
        const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString()
        localStorage.setItem('auth_token_expires_at', expiresAt)
      }
      lastRefreshAt = Date.now()
      console.log('✅ [Token] 자체 API로 Access Token 갱신 완료')
      return true
    }

    return false
  } catch (error) {
    console.error('❌ [Token] 갱신 중 오류:', error)
    return false
  }
  })()

  try {
    const result = await refreshInFlight
    return result
  } finally {
    refreshInFlight = null
  }
}

/**
 * API 호출 전 토큰 체크 및 갱신
 */
export const ensureValidToken = async (): Promise<string | null> => {
  const accessToken = localStorage.getItem('accessToken') || localStorage.getItem('auth_access_token')
  const refreshToken = localStorage.getItem('refreshToken') || localStorage.getItem('auth_refresh_token')

  if (!accessToken) {
    if (refreshToken) {
      const success = await refreshAccessToken()
      if (success) {
        return localStorage.getItem('accessToken')
      }
    }
    return null
  }

  if (isTokenExpiredOrExpiringSoon()) {
    const success = await refreshAccessToken()
    if (!success) {
      return null
    }
    return localStorage.getItem('accessToken')
  }

  return accessToken
}

// 토큰 갱신 타이머
let refreshTimer: NodeJS.Timeout | null = null

const scheduleTokenRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  const expiresAt = localStorage.getItem('auth_token_expires_at')
  if (!expiresAt) {
    return
  }

  const expiryTime = new Date(expiresAt).getTime()
  const now = Date.now()
  const fiveMinutes = 5 * 60 * 1000
  const refreshAfter = expiryTime - now - fiveMinutes

  if (refreshAfter <= 0) {
    refreshAccessToken().then(success => {
      if (success) scheduleTokenRefresh()
    })
  } else {
    refreshTimer = setTimeout(async () => {
      const success = await refreshAccessToken()
      if (success) {
        scheduleTokenRefresh()
      }
    }, refreshAfter)
  }
}

export const startTokenRefresh = async () => {
  const token = await ensureValidToken()
  if (token) {
    scheduleTokenRefresh()
  }
}

export const stopTokenRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}
